'use client';

import { FC, Fragment } from 'react';

import { useContentfulLiveUpdates } from '@contentful/live-preview/react';
import { Button, Heading } from '@purpur/library';

import { ButtonLink } from '@telialt/common-button-link/src';
import { RichTextContent } from '@telialt/common-rich-text-content/src/RichTextContent';

import { BlockCtaType } from '../../models/Content';
import { getBlockId } from '../../utils/content';
import { ImageLink } from '../common/ImageLink';
import { CtaProvider } from '../CtaProvider/CtaProvider';
import { ButtonsWrapper, DescriptionWrapper, StyledBlockCta, StyledBlockCtaContainer } from './BlockCta.styles';
import { transformBlockCta } from './transformers';

type Props = {
    contentItem: BlockCtaType;
    contained?: boolean;
};

export const BlockCta: FC<Props> = ({ contentItem: initialContentItem, contained }) => {
    const contentItem = useContentfulLiveUpdates(initialContentItem);
    const transformedContentItem = transformBlockCta(contentItem);

    const Comp = contained ? StyledBlockCtaContainer : Fragment;
    const hasContent = !!transformedContentItem.description || !!transformedContentItem.title;
    const isCentered = transformedContentItem.displayOption === 'centered';

    return (
        <Comp>
            <StyledBlockCta $boxed={hasContent} $centered={isCentered} id={getBlockId(contentItem)}>
                {hasContent && (
                    <DescriptionWrapper>
                        {!!transformedContentItem.title && (
                            <Heading tag="h2" variant="title-200">
                                {transformedContentItem.title}
                            </Heading>
                        )}
                        {!!transformedContentItem.description && (
                            <RichTextContent richText={transformedContentItem.description} />
                        )}
                    </DescriptionWrapper>
                )}
                <ButtonsWrapper>
                    {transformedContentItem.cta.map((cta, index) => (
                        <Fragment key={cta.id}>
                            {cta.image ? (
                                <ImageLink cta={cta} dataTest={`${transformedContentItem.id}-${cta.id}`} />
                            ) : (
                                <CtaProvider cta={cta}>
                                    {({ id, href, target, rel, text, onClick }) =>
                                        href ? (
                                            <ButtonLink
                                                variant={index === 0 ? 'primary' : 'secondary'}
                                                dataTest={`${transformedContentItem.id}-${id}`}
                                                href={href}
                                                target={target}
                                                rel={rel}
                                                onClick={onClick}
                                            >
                                                {text}
                                            </ButtonLink>
                                        ) : (
                                            <Button
                                                variant={index === 0 ? 'primary' : 'secondary'}
                                                data-test={`${transformedContentItem.id}-${id}`}
                                                onClick={onClick}
                                            >
                                                {text}
                                            </Button>
                                        )
                                    }
                                </CtaProvider>
                            )}
                        </Fragment>
                    ))}
                </ButtonsWrapper>
            </StyledBlockCta>
        </Comp>
    );
};
